/**
 * Backup-EXCLUDED key/value storage — a drop-in for the subset of
 * `@capacitor/preferences` the app uses (`get` / `set` / `remove` / `keys` /
 * `clear`, identical `{ key, value }` shapes).
 *
 * WHY: Preferences is `UserDefaults` on iOS (`Library/Preferences/*.plist`),
 * which iCloud/Finder backups include and restore onto another phone, and Apple
 * does not support excluding it. Cognito tokens and per-user screen caches
 * (`services/local-cache.ts`) must never leave the device. The point of this
 * module is backup/transfer exclusion; the iOS file-protection class
 * (CompleteUntilFirstUserAuthentication) is the same one UserDefaults already has.
 *
 * BACKENDS
 *  - iOS (native, `AscendSecureStorage` plugin present): files under
 *    `Application Support/ascend-secure/`, `isExcludedFromBackup`, explicit
 *    `NSFileProtectionCompleteUntilFirstUserAuthentication`
 *    (frontend/ios/App/App/SecureFileStore.swift).
 *  - Android: Capacitor Preferences (SharedPreferences). Already outside every
 *    backup / device-transfer path because AndroidManifest sets
 *    `allowBackup=false` + `dataExtractionRules` + `fullBackupContent`
 *    excluding all domains (frontend/native/android/res/xml/).
 *  - Web: Preferences' localStorage backend (browser storage; not backed up by us).
 *
 * MIGRATION (iOS only; one-way, loss-free, LEGACY WINS)
 *  Existing installs hold their data in Preferences. A legacy Preferences entry
 *  always wins over the secure copy: copy it into the secure store → read back
 *  and compare → only then delete the legacy key. "Legacy wins" is what keeps a
 *  downgrade → re-upgrade correct: the older build keeps writing Preferences, so
 *  a legacy entry that exists at upgrade time is by definition newer than any
 *  stale secure copy. A failed copy leaves the legacy value in place (never
 *  delete before the copy is verified), so an existing Cognito session survives.
 *
 *  Once per process, the first read of a secure-prefix key runs a SWEEP that
 *  migrates every legacy entry under {@link SECURE_KEY_PREFIXES}. After a clean
 *  sweep the legacy store is not consulted again for those keys (no extra bridge
 *  call per read). The "swept" state is deliberately IN MEMORY only: a persisted
 *  flag would survive a downgrade and hide the older build's newer writes.
 *  Writes go to the secure store and drop any legacy copy; removes clear both;
 *  `keys()` is the union so prefix wipes (sign-out) also reach legacy entries.
 *
 *  A secure-store READ error (e.g. file protection before first unlock) is
 *  propagated, never turned into "no value" — callers must not cache a miss.
 *
 * NOTE on the Capacitor proxy: plugin objects fabricate a `then`, so they are
 * never returned from / resolved through an async function here (see
 * services/biometric-auth-service.ts).
 */
import { Capacitor, registerPlugin } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'

interface AscendSecureStoragePlugin {
  get(options: { key: string }): Promise<{ value: string | null }>
  set(options: { key: string; value: string }): Promise<void>
  remove(options: { key: string }): Promise<void>
  keys(): Promise<{ keys: string[] }>
  clear(): Promise<void>
}

const PLUGIN_NAME = 'AscendSecureStorage'
const NativeSecure = registerPlugin<AscendSecureStoragePlugin>(PLUGIN_NAME)

/** True when keys live in the native backup-excluded store (iOS shell with the plugin). */
export function isNativeSecureStoreActive(): boolean {
  try {
    return Capacitor.getPlatform() === 'ios' && Capacitor.isPluginAvailable(PLUGIN_NAME)
  } catch {
    return false
  }
}

// Serialise every operation that can move/delete across the two stores so a
// sign-out `remove` can never interleave with a migrate-on-read and resurrect
// a deleted token.
let queue: Promise<unknown> = Promise.resolve()
function enqueue<T>(task: () => Promise<T>): Promise<T> {
  const next = queue.then(task, task)
  queue = next.catch(() => undefined)
  return next
}

async function secureGet(key: string): Promise<string | null> {
  const { value } = await NativeSecure.get({ key })
  return value ?? null
}

/** Legacy Preferences read that never throws (the legacy copy is best-effort). */
async function legacyGet(key: string): Promise<string | null> {
  try {
    const { value } = await Preferences.get({ key })
    return value ?? null
  } catch {
    return null
  }
}

async function legacyRemove(key: string): Promise<void> {
  try {
    await Preferences.remove({ key })
  } catch {
    /* best-effort */
  }
}

/** Key prefixes owned by SecureStorage callers (Amplify tokens, #475 screen cache, install secrets). */
export const SECURE_KEY_PREFIXES = ['amplify-auth.', 'ascend-cache:', 'ascend-install-secret:']

function isSecureKey(key: string): boolean {
  return SECURE_KEY_PREFIXES.some((p) => key.startsWith(p))
}

/**
 * Move one entry. LEGACY WINS: if a legacy Preferences value exists it is copied
 * into the secure store (overwriting a stale secure copy), read back to verify,
 * and only then deleted from Preferences. With no legacy value the secure value
 * is returned as-is (a secure READ error propagates).
 *
 * `strict` (sweep) throws when the secure copy fails; non-strict (read path)
 * serves the legacy value instead so a live session survives, leaving the legacy
 * entry in place for a later retry.
 */
async function migrateOne(key: string, strict: boolean): Promise<string | null> {
  const legacy = await legacyGet(key)
  if (legacy == null) return secureGet(key)
  try {
    await NativeSecure.set({ key, value: legacy })
    const check = await secureGet(key)
    if (check !== legacy) throw new Error(`Secure storage migrate verify failed for ${key}`)
  } catch (err) {
    if (strict) throw err
    return legacy
  }
  await legacyRemove(key)
  return legacy
}

// In-memory only (see header): true once this process finished a sweep with no failures.
let sweepPromise: Promise<boolean> | null = null
let sweptClean = false

function ensureSwept(): Promise<boolean> {
  if (!sweepPromise) {
    sweepPromise = migrateLegacyPreferences(SECURE_KEY_PREFIXES)
      .then((r) => {
        sweptClean = r.failed === 0 && r.listed
        return sweptClean
      })
      .catch(() => false)
  }
  return sweepPromise
}

/**
 * Run (once per process) the legacy → secure sweep for {@link SECURE_KEY_PREFIXES}.
 * Resolves true when everything legacy was moved. No-op (false) off iOS. Never throws.
 */
export function sweepLegacySecureKeys(): Promise<boolean> {
  if (!isNativeSecureStoreActive()) return Promise.resolve(false)
  return ensureSwept()
}

/** Test helper — forget the in-memory sweep state. */
export function __resetSecureStorageForTests(): void {
  sweepPromise = null
  sweptClean = false
  queue = Promise.resolve()
}

export const SecureStorage = {
  async get(options: { key: string }): Promise<{ value: string | null }> {
    if (!isNativeSecureStoreActive()) return Preferences.get(options)
    if (isSecureKey(options.key) && (await ensureSwept())) {
      // Clean sweep done this process: the secure store is authoritative.
      return { value: await secureGet(options.key) }
    }
    // Sweep incomplete / non-owned key: legacy-wins, serialized with writes so a
    // concurrent remove()/set() beats a stale legacy read.
    return enqueue(async () => ({ value: await migrateOne(options.key, false) }))
  },

  async set(options: { key: string; value: string }): Promise<void> {
    if (!isNativeSecureStoreActive()) return Preferences.set(options)
    return enqueue(async () => {
      await NativeSecure.set(options)
      if (!(sweptClean && isSecureKey(options.key))) await legacyRemove(options.key)
    })
  },

  async remove(options: { key: string }): Promise<void> {
    if (!isNativeSecureStoreActive()) return Preferences.remove(options)
    return enqueue(async () => {
      await NativeSecure.remove(options)
      await legacyRemove(options.key)
    })
  },

  /** Secure keys ∪ legacy Preferences keys (so prefix wipes reach un-migrated data). Throws if the secure listing fails. */
  async keys(): Promise<{ keys: string[] }> {
    if (!isNativeSecureStoreActive()) return Preferences.keys()
    const [secure, legacy] = await Promise.all([
      NativeSecure.keys(),
      Preferences.keys().catch(() => ({ keys: [] as string[] })),
    ])
    return { keys: [...new Set([...secure.keys, ...legacy.keys])] }
  },

  /**
   * Remove every SECURE entry. On native this deliberately does NOT clear the
   * legacy Preferences store (it also holds unrelated, non-sensitive settings);
   * wipe by prefix via `keys()` + `remove()` when legacy entries must go too.
   */
  async clear(): Promise<void> {
    if (!isNativeSecureStoreActive()) return Preferences.clear()
    return enqueue(async () => {
      await NativeSecure.clear()
    })
  },
}

/**
 * Eagerly move every legacy Preferences entry whose key starts with one of
 * `prefixes` into the secure store (iOS only; no-op elsewhere), LEGACY WINS.
 * Each key is copied, verified, and only then deleted from Preferences; a key
 * that fails stays put and is retried by the next sweep / read. Never throws.
 * `listed` is false when the legacy key listing itself failed (sweep not proven).
 */
export async function migrateLegacyPreferences(
  prefixes: string[],
): Promise<{ moved: number; failed: number; listed: boolean }> {
  if (!isNativeSecureStoreActive()) return { moved: 0, failed: 0, listed: false }
  let moved = 0
  let failed = 0
  let legacyKeys: string[]
  try {
    legacyKeys = (await Preferences.keys()).keys.filter((k) => prefixes.some((p) => k.startsWith(p)))
  } catch {
    return { moved, failed, listed: false }
  }
  for (const key of legacyKeys) {
    try {
      // Only entries that actually still have a legacy value count as moved.
      const legacy = await legacyGet(key)
      if (legacy == null) continue
      await enqueue(() => migrateOne(key, true))
      moved += 1
    } catch {
      failed += 1
    }
  }
  return { moved, failed, listed: true }
}
